'use client';

import React from 'react';
import { SuggestionItem } from './slash-command';

interface SlashCommandPaletteProps {
    isOpen: boolean;
    position: { x: number; y: number; isAbove: boolean } | null;
    items: SuggestionItem[];
    query: string;
    onQueryChange: (query: string) => void;
    selected: number;
    onSelectItem: (index: number) => void;
    onExecuteItem: (item: SuggestionItem) => void;
    menuRef: React.Ref<HTMLDivElement>;
    inputRef: React.Ref<HTMLInputElement>;
}

/**
 * Renders the slash command palette overlay with search and command list.
 */
export const SlashCommandPalette: React.FC<SlashCommandPaletteProps> = ({
    isOpen,
    position,
    items,
    query,
    onQueryChange,
    selected,
    onSelectItem,
    onExecuteItem,
    menuRef,
    inputRef,
}) => {
    if (!isOpen || !position) {
        return null;
    }

    return (
        <div
            ref={menuRef}
            className={`slash-command-palette z-[100] h-auto max-h-[400px] w-72 rounded-md border border-stone-200 bg-white shadow-md transition-all flex ${position.isAbove ? 'flex-col-reverse' : 'flex-col'}`}
            style={{
                position: 'fixed',
                left: position.x,
                ...(position.isAbove
                    ? { bottom: `${window.innerHeight - position.y}px` }
                    : { top: position.y })
            }}
        >
            {/* Search Input */}
            <div className={`p-2 ${position.isAbove ? 'border-t' : 'border-b'} border-stone-200`}>
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => onQueryChange(e.target.value)}
                    placeholder="Search commands..."
                    className="w-full px-3 py-2 text-sm border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
            </div>

            {/* Command List */}
            <div className="overflow-y-auto px-1 py-2 flex-1">
                {items.length === 0 ? (
                    <div className="px-2 text-stone-500">No results</div>
                ) : (
                    <div role="listbox">
                        {items.map((item, idx) => (
                            <div
                                key={item.title}
                                role="option"
                                aria-selected={idx === selected}
                                className={`flex w-full items-center space-x-2 rounded-md px-2 py-1 text-left text-sm hover:bg-stone-100 ${idx === selected ? 'bg-stone-100' : ''}`}
                                onMouseEnter={() => onSelectItem(idx)}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onExecuteItem(item);
                                }}
                            >
                                <div className="flex h-10 w-10 items-center justify-center rounded-md border border-stone-200 bg-white">
                                    {item.icon}
                                </div>
                                <div>
                                    <p className="font-medium">{item.title}</p>
                                    <p className="text-xs text-stone-500">{item.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SlashCommandPalette;
